import React from 'react';
import { motion } from 'framer-motion';

interface RadarScopeProps {
    /** 스코프 지름(px). 기본 256 */
    size?: number;
    className?: string;
}

// 레이더가 지나갈 때 깜빡이는 감지 신호(blip) 위치/타이밍
const BLIPS = [
    { top: '28%', left: '64%', delay: 0.0, color: '#6effb0' },
    { top: '58%', left: '38%', delay: 0.7, color: '#02d1ab' },
    { top: '44%', left: '72%', delay: 1.3, color: '#4FC3F7' },
    { top: '70%', left: '60%', delay: 1.9, color: '#6effb0' },
    { top: '36%', left: '34%', delay: 2.5, color: '#02d1ab' },
];

/**
 * 도는 레이더 스코프 비주얼.
 * 동심원 그리드 + 회전 스윕 빔 + 깜빡이는 감지 신호로 "관심 계정의 변화를 감지한다"를 시각화한다.
 * 랜딩(크게)·대시보드(작게)에서 size prop으로 재사용.
 */
const RadarScope: React.FC<RadarScopeProps> = ({ size = 256, className = '' }) => {
    return (
        <div
            className={`relative ${className}`}
            style={{ width: size, height: size }}
            aria-label="관심 계정 변화 감지 레이더"
            role="img"
        >
            {/* 외곽 발광 */}
            <div className="pointer-events-none absolute inset-0 rounded-full bg-[#02d1ab]/15 blur-2xl" />

            {/* 스코프 베이스 */}
            <div className="absolute inset-0 rounded-full border border-[#02d1ab]/30 bg-gradient-to-br from-[#02d1ab]/[0.06] to-transparent backdrop-blur-sm" />

            {/* 동심원 그리드 */}
            {[0.74, 0.5, 0.26].map((scale) => (
                <div
                    key={scale}
                    className="absolute rounded-full border border-[#02d1ab]/20"
                    style={{
                        inset: `${((1 - scale) / 2) * 100}%`,
                    }}
                />
            ))}

            {/* 십자선 */}
            <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-[#02d1ab]/15" />
            <div className="absolute top-1/2 left-0 w-full h-px -translate-y-1/2 bg-[#02d1ab]/15" />

            {/* 회전하는 스윕 빔 */}
            <div
                className="absolute inset-0 rounded-full animate-[spin_3.4s_linear_infinite]"
                style={{
                    background:
                        'conic-gradient(from 0deg, transparent 0deg, transparent 250deg, rgba(2,209,171,0.04) 300deg, rgba(110,255,176,0.32) 352deg, rgba(110,255,176,0.55) 360deg)',
                    WebkitMaskImage: 'radial-gradient(circle, #000 62%, transparent 63%)',
                    maskImage: 'radial-gradient(circle, #000 62%, transparent 63%)',
                }}
            />

            {/* 감지 신호 blip */}
            {BLIPS.map((b) => (
                <motion.span
                    key={`${b.top}-${b.left}`}
                    className="absolute block rounded-full"
                    style={{
                        top: b.top,
                        left: b.left,
                        width: 8,
                        height: 8,
                        background: b.color,
                        boxShadow: `0 0 12px ${b.color}`,
                    }}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: [0, 1, 0], scale: [0.4, 1.5, 0.4] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: b.delay, ease: 'easeOut' }}
                />
            ))}

            {/* 중앙 코어 */}
            <div className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6effb0] shadow-[0_0_16px_rgba(110,255,176,0.9)]" />
            <motion.div
                className="absolute left-1/2 top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#6effb0]"
                animate={{ scale: [1, 3.2], opacity: [0.6, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeOut' }}
            />
        </div>
    );
};

export default RadarScope;
